const formatLessonsData = (prismicData, defaultPdfLinkText = "Lataa PDF") => {
  const { allPrismicLesson, allPrismicGradeGroup, freeLessonPdfs } = prismicData

  // Return empty structure if no Prismic data (fallback mode)
  if (!allPrismicLesson || !allPrismicGradeGroup) {
    return null
  }

  // The page query only fetches PDF URLs for free lessons, so a gated lesson
  // has no URL to look up here. Keep it that way: page-data.json publishes
  // whatever the query returns, whether or not it is ever rendered.
  const freePdfUrlById = new Map(
    (freeLessonPdfs?.nodes || []).map((node) => [
      node.id,
      node.data?.pdf_link?.url || "",
    ]),
  )

  // Group lessons by grade group
  const lessonsByGroup = {}
  allPrismicLesson.nodes.forEach((lesson) => {
    // Skip inactive lessons
    if (lesson.data.status === "archived" || lesson.data.status === "draft") {
      return
    }

    const gradeGroup = lesson.data.grade_group
    if (!lessonsByGroup[gradeGroup]) {
      lessonsByGroup[gradeGroup] = []
    }

    // Parse description from raw StructuredText to extract individual paragraphs
    let descriptionArray = []
    if (lesson.data.description?.raw) {
      try {
        const rawData =
          typeof lesson.data.description.raw === "string"
            ? JSON.parse(lesson.data.description.raw)
            : lesson.data.description.raw

        // Extract text from paragraph blocks
        descriptionArray = rawData
          .filter((block) => block.type === "paragraph")
          .map((block) => block.text)
          .filter(Boolean)
      } catch (error) {
        console.warn("Error parsing lesson description:", error)
        descriptionArray = []
      }
    }

    lessonsByGroup[gradeGroup].push({
      title: lesson.data.title?.text || "",
      description: descriptionArray,
      videoLink: lesson.data.video_id || "",
      link: freePdfUrlById.get(lesson.id) || "",
      linkText: defaultPdfLinkText,
      duration: lesson.data.duration || "",
      coverImage: lesson.data.cover?.gatsbyImageData || null,
      free: lesson.data.free || false,
    })
  })

  // Format grade groups with lessons
  const sections = allPrismicGradeGroup.nodes
    .filter(
      (group) =>
        lessonsByGroup[group.data.slug] &&
        lessonsByGroup[group.data.slug].length > 0,
    )
    .map((group) => ({
      id: group.data.slug,
      title: group.data.title?.text || "",
      description: group.data.description?.text || "",
      color: group.data.color_theme || "persona-color-1",
      lectures: lessonsByGroup[group.data.slug] || [],
    }))

  return {
    sections,
  }
}

// Extract page-level data from translations
function getPageDataFromTranslations(translations) {
  if (!translations) return {}

  const {
    materialsTitle,
    materialsDescription,
    materialsIntro1,
    materialsIntro2,
    materialsIntro3,
    materialsIntro4,
    materialsIntroLink,
    materialsCuricculumTitle,
    materialsCuricculum1,
    materialsCuricculum2,
    materialsCuricculum3,
    materialsCuricculum4,
    materialsCuricculum5,
    materialsCuricculum6,
    materialsCuricculum7,
    materialsCuricculum8,
    materialsCuricculum9,
  } = translations

  return {
    title: materialsTitle || "",
    description: materialsDescription || "",
    introText: [
      materialsIntro1,
      materialsIntro2,
      materialsIntro3,
      materialsIntro4,
    ],
    contactLinkText: materialsIntroLink || "",
    curriculum: {
      title: materialsCuricculumTitle || "",
      items: [
        materialsCuricculum1,
        materialsCuricculum2,
        materialsCuricculum3,
        materialsCuricculum4,
        materialsCuricculum5,
        materialsCuricculum6,
        materialsCuricculum7,
        materialsCuricculum8,
        materialsCuricculum9,
      ].filter(Boolean),
    },
  }
}

export const getLessonsData = (prismicData, translations) => {
  // Always get page data from translations (not migrated to Prismic)
  const pageDataFromTranslations = getPageDataFromTranslations(translations)

  // Try Prismic data for lessons first
  const prismicLessons = formatLessonsData(
    prismicData,
    translations?.materialsPdfLinkText,
  )

  if (prismicLessons) {
    return {
      sections: prismicLessons.sections,
      pageData: pageDataFromTranslations,
    }
  }
  return null
}
